import { useEffect, useRef, useState } from 'react';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import { fetchGuestToken } from '../../api/reports';

interface EmbeddedDashboardProps {
  dashboardId: string;
  supersetUrl: string;
  height?: string;
}

export default function EmbeddedDashboard({
  dashboardId,
  supersetUrl,
  height = 'calc(100vh - 220px)',
}: EmbeddedDashboardProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!containerRef.current || !dashboardId || !supersetUrl) return;

    setLoading(true);
    setError(null);

    const mountPoint = containerRef.current;

    embedDashboard({
      id: dashboardId,
      supersetDomain: supersetUrl,
      mountPoint,
      fetchGuestToken: () => fetchGuestToken(dashboardId),
      dashboardUiConfig: {
        hideTitle: true,
        hideChartControls: false,
        hideTab: false,
        filters: {
          visible: true,
          expanded: false,
        },
      },
    })
      .then(() => {
        setLoading(false);
        // Style the iframe to fill the container
        const iframe = mountPoint.querySelector('iframe');
        if (iframe) {
          iframe.style.width = '100%';
          iframe.style.height = height;
          iframe.style.border = 'none';
          iframe.style.borderRadius = '8px';
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to embed dashboard');
        setLoading(false);
      });

    // Cleanup on unmount
    return () => {
      mountPoint.innerHTML = '';
    };
  }, [dashboardId, supersetUrl, height]);

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <p className="text-sm text-red-700 dark:text-red-400 font-medium">Failed to load dashboard</p>
        <p className="text-xs text-red-600 dark:text-red-500 mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-[#1a1f1c] rounded-lg z-10">
          <div className="w-8 h-8 border-2 border-moss-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      <div
        ref={containerRef}
        className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-hidden"
        style={{ minHeight: height }}
      />
    </div>
  );
}
